import React, { useEffect, useState } from 'react';
import { Box, Stack } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useIntersectionObserver } from 'usehooks-ts';
import { MenRunningAnimation } from '@common/assets';
import { BarGraphRuler } from '../BarGraphRuler';
import { GoalIndicator } from '../GoalIndicator';
import {
  getStudentWeeklyAchievementsTotal,
  getStudentDailyAchievementsForWeek,
} from './weeklyProgressGraphUtils';
import {
  getRulerSize,
  WEEKLY_PROGRESS_RULER_SIZE,
  getGoalIndicatorAnimation,
  getProgressBarAnimation,
} from '../utils';
import { LottiePlayer, Container, Heading, ProgressBar } from '../GraphStyledElements';
import { type StudentDayStats } from '../../model';
import { ProgressPopup } from '../ProgressPopup';

const ANIMATION_DELAY_TIME = 2000;

const Bar = styled(ProgressBar)(
  ({
    color,
    progress,
    delay,
    first,
    last,
  }: {
    color: string;
    progress: number;
    delay: number;
    first: string;
    last: string;
  }) => ({
    width: `${Math.max(0, Math.min(progress, 100)) ?? '0'}%`,
    background: color ?? '#b7ccff',
    animationName: getProgressBarAnimation(progress),
    animationDelay: `${delay ?? 300}ms`,
    borderRadius: 0,
    borderTopLeftRadius: first === 'true' ? '12px' : 0,
    borderBottomLeftRadius: first === 'true' ? '12px' : 0,
    borderTopRightRadius: last === 'true' ? '12px' : 0,
    borderBottomRightRadius: last === 'true' ? '12px' : 0,
    cursor: 'pointer',
  })
);

type Props = {
  personalGoal: number | null | undefined;
  data: Record<string, StudentDayStats> | undefined;
  completedLessons: number;
  weeklyGoal: number;
  weeklyPersonalGoal: number | null | undefined;
};

export const WeeklyProgressGraph = ({
  data,
  personalGoal,
  completedLessons,
  weeklyGoal,
  weeklyPersonalGoal,
}: Props) => {
  const [animation, setAnimation] = useState<any>(MenRunningAnimation);
  const [progressPopupKey, setProgressPopupKey] = useState<string | null>(null);

  const { isIntersecting, ref } = useIntersectionObserver({
    threshold: 0.5,
    freezeOnceVisible: true,
  });

 

  const { rulerSize, rulerInterval } = getRulerSize(
    completedLessons,
    weeklyPersonalGoal,
    WEEKLY_PROGRESS_RULER_SIZE
  );
  const weeklyAchievements = getStudentDailyAchievementsForWeek(data, rulerSize);

  useEffect(() => {
    if (!isIntersecting) {
      return;
    }

    const animation = getGoalIndicatorAnimation({
      completedLessons,
      goal: weeklyGoal,
      personalGoal: weeklyPersonalGoal,
    });

    const timeout = setTimeout(() => {
      setAnimation(animation);
    }, ANIMATION_DELAY_TIME + 2500);

    return () => {
      clearTimeout(timeout);
    };
  }, [isIntersecting, weeklyPersonalGoal, weeklyGoal, data?.actual]);

  return (
    <Container ref={ref}>
      <Heading>This Week</Heading>
      <Box position="relative">
        <Stack direction="row" minHeight="48px">
          {isIntersecting &&
            weeklyAchievements.map((achievement, i) => {
              const isSelected = progressPopupKey === achievement.day;

              return (
                <Bar
                  position="relative"
                  key={achievement.day}
                  color={isSelected ? '#EA0BBD' : achievement.color}
                  progress={achievement.progress}
                  delay={ANIMATION_DELAY_TIME + i * 1000}
                  first={(i === 0).toString()}
                  last={(i === weeklyAchievements.length - 1).toString()}
                  onClick={() => setProgressPopupKey(achievement.day)}
                >
                  <ProgressPopup
                    achievement={achievement}
                    onClose={() => setProgressPopupKey(null)}
                    visible={isSelected}
                  />
                </Bar>
              );
            })}
          <LottiePlayer autoplay loop src={animation} />
        </Stack>
        <BarGraphRuler size={rulerSize} interval={rulerInterval} />
        {personalGoal !== weeklyGoal && (
          <GoalIndicator
            type="todays_goal"
            heading="Weekly Goal"
            rulerSize={rulerSize}
            goal={weeklyGoal}
          />
        )}
        {weeklyPersonalGoal && (
          <GoalIndicator type="personal_goal" rulerSize={rulerSize} goal={weeklyPersonalGoal} />
        )}
      </Box>
    </Container>
  );
};
